import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

type GrowthGuideProps = {
  defaultSrc?: string;
  defaultImgDir?: string;
  defaultPageCount?: number;
  defaultImgExt?: "jpg" | "png" | "webp";
};

export default function GrowthGuideComponent({
  defaultSrc = "/tools/growth-guide-for-gyms.pdf",
  defaultImgDir = "/tools/growth-guide-img",
  defaultPageCount = 9,
  defaultImgExt = "jpg",
}: GrowthGuideProps) {
  const [params] = useSearchParams();

  // ---- Inputs (PDF + images) ----------------------------------------------
  const pdfUrl = useMemo(() => {
    const paramUrl = params.get("pdf") || "";
    return paramUrl ? decodeURIComponent(paramUrl) : defaultSrc;
  }, [params, defaultSrc]);

  const imgDir = useMemo(
    () => params.get("imgdir") || defaultImgDir,
    [params, defaultImgDir]
  );

  const pageCount = useMemo(() => {
    const v = Number(params.get("pages"));
    return Number.isFinite(v) && v > 0 ? v : defaultPageCount;
  }, [params, defaultPageCount]);

  const imgExt = useMemo(() => {
    const ext = (params.get("ext") || defaultImgExt).toLowerCase();
    return (["jpg", "png", "webp"] as const).includes(ext as any)
      ? (ext as "jpg" | "png" | "webp")
      : defaultImgExt;
  }, [params, defaultImgExt]);

  const images = useMemo(
    () =>
      Array.from(
        { length: pageCount },
        (_, i) => `${imgDir}/${i + 1}.${imgExt}`
      ),
    [imgDir, pageCount, imgExt]
  );

  const fileName = useMemo(() => {
    try {
      const u = new URL(pdfUrl, window.location.origin);
      const name = u.pathname.split("/").pop() || "document.pdf";
      return name.split("?")[0];
    } catch {
      return "document.pdf";
    }
  }, [pdfUrl]);

  // ---- Theme tokens --------------------------------------------------------
  const c = {
    bg: "var(--bg-elev-1, #0f141a)",
    bg2: "var(--bg-elev-2, #111820)",
    text: "var(--text-primary, #e6e9ef)",
    textMuted: "var(--text-muted, #a9b2c3)",
    border: "var(--border, rgba(255,255,255,0.08))",
    borderStrong: "var(--border-strong, rgba(255,255,255,0.12))",
    btnBg: "var(--btn-bg, rgba(255,255,255,0.04))",
    btnBgHover: "var(--btn-bg-hover, rgba(255,255,255,0.08))",
    accent: "var(--accent, #59ffa7)",
    focus: "var(--focus, #59ffa7)",
  };

  const buttonStyle: React.CSSProperties = {
    padding: "10px 12px",
    border: `1px solid ${c.border}`,
    borderRadius: 10,
    textDecoration: "none",
    background: c.btnBg,
    color: c.text,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    transition:
      "background 120ms ease, border-color 120ms ease, transform 60ms ease",
    WebkitTapHighlightColor: "transparent",
  };

  // ---- Layout helpers ------------------------------------------------------
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const set = () => setIsNarrow(mq.matches);
    set();
    mq.addEventListener?.("change", set);
    return () => mq.removeEventListener?.("change", set);
  }, []);

  // ---- Hover Tools (Open/Download) ----------------------------------------
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement | null>(null);
  const isTouch =
    typeof window !== "undefined" && matchMedia("(hover: none)").matches;

  useEffect(() => {
    if (!isTouch) return; // desktop hover-only; mobile uses tap to toggle
    const closeOnEsc = (e: KeyboardEvent) =>
      e.key === "Escape" && setToolsOpen(false);
    const closeOnClickOutside = (e: MouseEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) {
        setToolsOpen(false);
      }
    };
    window.addEventListener("keydown", closeOnEsc);
    window.addEventListener("click", closeOnClickOutside);
    return () => {
      window.removeEventListener("keydown", closeOnEsc);
      window.removeEventListener("click", closeOnClickOutside);
    };
  }, [isTouch]);

  // ---- Scroll hint (only on landing) --------------------------------------
  const [showScrollHint, setShowScrollHint] = useState(true);
  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 10) setShowScrollHint(false);
    };
    const timer = window.setTimeout(() => setShowScrollHint(false), 6000);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.clearTimeout(timer);
    };
  }, []);

  return (
    <div
      style={{
        maxWidth: 960,
        margin: "0 auto",
        padding: 16,
        color: c.text,
        position: "relative",
      }}
    >
      {/* Image stack */}
      <div
        style={{
          border: `1px solid ${c.borderStrong}`,
          borderRadius: 12,
          background: c.bg2,
          overflow: "hidden",
        }}
      >
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: isNarrow ? 8 : 12,
            display: "grid",
            gap: isNarrow ? 8 : 12,
          }}
        >
          {images.map((src, i) => (
            <li
              key={src}
              style={{
                border: `1px solid ${c.border}`,
                borderRadius: 12,
                background: c.bg,
                overflow: "hidden",
              }}
            >
              <img
                src={src}
                alt={`Growth Guide page ${i + 1}`}
                loading="lazy"
                decoding="async"
                style={{
                  display: "block",
                  width: "100%",
                  height: "auto",
                  verticalAlign: "middle",
                }}
                onError={(e) => {
                  const el = e.currentTarget;
                  el.style.display = "none";
                  const p = el.parentElement;
                  if (p) (p as HTMLElement).style.display = "none";
                }}
              />
            </li>
          ))}
        </ul>
      </div>

      {/* Floating hover FAB w/ modal */}
      <div
        ref={toolsRef}
        style={{
          position: "fixed",
          right: 16,
          bottom: 16,
          zIndex: 50,
        }}
        onMouseEnter={() => !isTouch && setToolsOpen(true)}
        onMouseLeave={() => !isTouch && setToolsOpen(false)}
      >
        {/* FAB */}
        <button
          aria-label="More options"
          onClick={() => isTouch && setToolsOpen((v) => !v)}
          style={{
            appearance: "none",
            border: `1px solid ${c.borderStrong}`,
            background: c.bg,
            color: c.text,
            borderRadius: 999,
            width: 48,
            height: 48,
            cursor: "pointer",
            boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
          }}
        >
          ⋮
        </button>

        {/* Modal card */}
        <div
          style={{
            position: "absolute",
            right: 0,
            bottom: 56,
            background: c.bg,
            border: `1px solid ${c.borderStrong}`,
            borderRadius: 12,
            padding: 10,
            boxShadow: "0 12px 24px rgba(0,0,0,0.5)",
            display: toolsOpen ? "block" : "none",
            minWidth: 200,
          }}
        >
          <div style={{ display: "grid", gap: 8 }}>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noreferrer"
              style={buttonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = c.btnBgHover;
                e.currentTarget.style.borderColor = c.borderStrong;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = c.btnBg;
                e.currentTarget.style.borderColor = c.border;
              }}
              title="Open PDF in new tab"
            >
              Open PDF
            </a>
            <a
              href={pdfUrl}
              download={fileName}
              style={buttonStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = c.btnBgHover;
                e.currentTarget.style.borderColor = c.borderStrong;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = c.btnBg;
                e.currentTarget.style.borderColor = c.border;
              }}
              title="Download PDF"
            >
              Download PDF
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
